/**
 * AHP (Analytic Hierarchy Process) Calculation Engine
 * 
 * Implements the core mathematical logic for AHP:
 * - Pairwise comparison matrix construction
 * - Principal eigenvector calculation (priority vector)
 * - Consistency checking (CI, CR, lambda_max)
 * 
 * Based on Saaty's AHP methodology
 */

export interface AHPResult {
  weights: number[];
  lambda_max: number;
  consistency_index: number;
  consistency_ratio: number;
  is_consistent: boolean;
}

/**
 * Random Index (RI) values from Saaty's standard table
 * Used for calculating Consistency Ratio
 */
const RANDOM_INDEX: Record<number, number> = {
  1: 0.00,
  2: 0.00,
  3: 0.58,
  4: 0.90,
  5: 1.12,
  6: 1.24,
  7: 1.32,
  8: 1.41,
  9: 1.45,
};

/**
 * Calculate AHP priority vector and consistency metrics
 * 
 * Uses the Mean of Normalized Values method (approximation of Principal Eigenvector)
 * This is computationally efficient (O(n²)) and sufficient for matrices where n < 10
 * 
 * @param matrix - n×n pairwise comparison matrix (must be reciprocal and have 1s on diagonal)
 * @returns AHPResult with weights, lambda_max, CI, CR, and consistency status
 */
export function calculateAHP(matrix: number[][]): AHPResult {
  const n = matrix.length;
  
  // Validate matrix dimensions
  if (n === 0) {
    throw new Error('Matrix cannot be empty');
  }
  
  if (matrix.some(row => row.length !== n)) {
    throw new Error('Matrix must be square');
  }
  
  // Validate matrix properties
  for (let i = 0; i < n; i++) {
    if (matrix[i][i] !== 1) {
      throw new Error(`Diagonal element at [${i}][${i}] must be 1`);
    }
    for (let j = 0; j < n; j++) {
      if (i !== j && Math.abs(matrix[i][j] * matrix[j][i] - 1) > 0.001) {
        throw new Error(`Matrix must be reciprocal: [${i}][${j}] * [${j}][${i}] should equal 1`);
      }
    }
  }
  
  // Step 1: Normalize the matrix (column-wise)
  // Divide each element by the sum of its column
  const columnSums: number[] = [];
  for (let j = 0; j < n; j++) {
    let sum = 0;
    for (let i = 0; i < n; i++) {
      sum += matrix[i][j];
    }
    columnSums[j] = sum;
  }
  
  const normalizedMatrix: number[][] = [];
  for (let i = 0; i < n; i++) {
    normalizedMatrix[i] = [];
    for (let j = 0; j < n; j++) {
      normalizedMatrix[i][j] = matrix[i][j] / columnSums[j];
    }
  }
  
  // Step 2: Calculate Priority Vector (weights)
  // Average the values in each row
  const weights: number[] = [];
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let j = 0; j < n; j++) {
      sum += normalizedMatrix[i][j];
    }
    weights[i] = sum / n;
  }
  
  // Step 3: Calculate Principal Eigenvalue (lambda_max)
  // Multiply original matrix by priority vector to get weighted sum vector
  const weightedSumVector: number[] = [];
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let j = 0; j < n; j++) {
      sum += matrix[i][j] * weights[j];
    }
    weightedSumVector[i] = sum;
  }
  
  // Divide weighted sum elements by priority vector elements
  const lambdaValues: number[] = [];
  for (let i = 0; i < n; i++) {
    lambdaValues[i] = weightedSumVector[i] / weights[i];
  }
  
  // Average these values to get lambda_max
  const lambda_max = lambdaValues.reduce((sum, val) => sum + val, 0) / n;
  
  // Step 4: Calculate Consistency Index (CI)
  const ci = (lambda_max - n) / (n - 1);
  
  // Step 5: Calculate Consistency Ratio (CR)
  const ri = RANDOM_INDEX[n] ?? 1.49; // Default for n > 9
  const cr = ri !== 0 ? ci / ri : 0.0;
  
  // The golden rule: CR < 0.10 means judgments are consistent
  const is_consistent = cr < 0.10;
  
  return {
    weights,
    lambda_max,
    consistency_index: ci,
    consistency_ratio: cr,
    is_consistent,
  };
}

/**
 * Build a pairwise comparison matrix from user votes
 * 
 * @param items - Array of item names/IDs
 * @param votes - Array of {row, col, value} objects representing user comparisons
 * @returns n×n reciprocal matrix
 */
export function buildComparisonMatrix(
  items: string[],
  votes: Array<{ row: number; col: number; value: number }>
): number[][] {
  const n = items.length;
  const matrix: number[][] = [];
  
  // Initialize matrix with 1s on diagonal
  for (let i = 0; i < n; i++) {
    matrix[i] = [];
    for (let j = 0; j < n; j++) {
      matrix[i][j] = i === j ? 1 : 0; // Will be filled by votes
    }
  }
  
  // Fill matrix from votes
  for (const vote of votes) {
    const { row, col, value } = vote;
    if (row >= 0 && row < n && col >= 0 && col < n) {
      matrix[row][col] = value;
      // Set reciprocal value
      if (value !== 0) {
        matrix[col][row] = 1 / value;
      }
    }
  }
  
  // Fill any remaining zeros (shouldn't happen in complete matrix)
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (matrix[i][j] === 0 && i !== j) {
        // If we have the reciprocal, use it
        if (matrix[j][i] !== 0) {
          matrix[i][j] = 1 / matrix[j][i];
        }
      }
    }
  }
  
  return matrix;
}

/**
 * Convert Saaty scale value (1-9) to descriptive label
 */
export function getSaatyLabel(value: number): string {
  const labels: Record<number, string> = {
    1: 'Equal',
    2: 'Equal to Moderate',
    3: 'Moderate',
    4: 'Moderate to Strong',
    5: 'Strong',
    6: 'Strong to Very Strong',
    7: 'Very Strong',
    8: 'Very Strong to Extreme',
    9: 'Extreme',
  };
  
  return labels[value] || 'Unknown';
}

/**
 * Get Saaty scale value from slider position
 * Slider range: -9 to 9, where 0 = equal (1), positive = left preferred, negative = right preferred
 */
export function sliderToSaatyValue(sliderValue: number): number {
  if (sliderValue === 0) return 1;
  if (sliderValue > 0) return sliderValue;
  return 1 / Math.abs(sliderValue);
}

/**
 * Convert Saaty value to slider position
 */
export function saatyValueToSlider(saatyValue: number): number {
  if (saatyValue === 1) return 0;
  if (saatyValue >= 1) return saatyValue;
  return -1 / saatyValue;
}


